import React, { useMemo } from 'react';
import { Select, message } from 'antd';
import s from './s.module.less';
import { TimeItemType } from 'types/appointmentSetting';

type TimeRangerPickerProps = {
    value?: TimeItemType,
    onChange?: (val: TimeItemType) => void, //start and end time
    disabledTimes?: TimeItemType[], // selected time
};

type TimeOption = {
    label: string,
    value: number[],
};

const TimePicker = (props: TimeRangerPickerProps) => {
    const { onChange, value, disabledTimes = [] } = props;
    const [start, setStart] = React.useState('');
    const [end, setEnd] = React.useState('');
    const time = [0, 15, 30, 45];

    // 合并相邻的时间段
    const mergeAdjacentTimeRanges = () => {
        if (!disabledTimes || disabledTimes.length === 0) return [];

        const timeRanges = disabledTimes
            .filter((item) => item.fromHour !== undefined && item.toHour !== undefined)
            .map((item) => ({
                start: item.fromHour! * 60 + (item.fromMinute || 0),
                end: item.toHour! * 60 + (item.toMinute || 0),
            }))
            .sort((a, b) => a.start - b.start);

        if (timeRanges.length === 0) return [];

        const mergedRanges = [timeRanges[0]];

        for (let i = 1; i < timeRanges.length; i++) {
            const currentRange = timeRanges[i];
            const lastMergedRange = mergedRanges[mergedRanges.length - 1];

            if (currentRange.start <= lastMergedRange.end) {
                lastMergedRange.end = Math.max(lastMergedRange.end, currentRange.end);
            } else {
                mergedRanges.push(currentRange);
            }
        }

        return mergedRanges;
    };

    const mergedTimeRanges = useMemo(() => mergeAdjacentTimeRanges(), [disabledTimes]);

    // 检查时间点是否在禁用时间段内
    const isTimeInDisabledRanges = (hour: number, minute: number) => {
        const currentTime = hour * 60 + minute;
        return mergedTimeRanges.some((range) =>
            currentTime > range.start && currentTime < range.end,
        );
    };

    const isStartTimeDisabled = (hour: number, minute: number) => {
        const currentTime = hour * 60 + minute;

        if (isTimeInDisabledRanges(hour, minute)) {
            return true;
        }

        if (currentTime === 0 && mergedTimeRanges[0]?.start === 0) {
            return true;
        }

        if (end) {
            const [endHour, endMinute] = end.split(',').map((v) => parseInt(v, 10));
            const endTime = endHour * 60 + endMinute;

            // 开始时间不能晚于或等于结束时间
            if (currentTime >= endTime) {
                return true;
            }

            return mergedTimeRanges.some((range) => {
                // 例如，如果已有时间段是1:30-2:00，允许选择1:00-1:30
                if (endTime === range.start) {
                    return false;
                }

                return (currentTime < range.end && endTime > range.start);
            });
        }

        return false;
    };

    const isEndTimeDisabled = (hour: number, minute: number) => {
        const currentTime = hour * 60 + minute;

        if (isTimeInDisabledRanges(hour, minute)) {
            return true;
        }

        if (start) {
            const [startHour, startMinute] = start.split(',').map((v) => parseInt(v, 10));
            const startTime = startHour * 60 + startMinute;

            // 结束时间不能早于或等于开始时间
            if (currentTime <= startTime) {
                return true;
            }

            return mergedTimeRanges.some((range) => {
                // 例如，如果已有时间段是1:00-1:30，允许选择1:30-2:00
                if (currentTime === range.end) {
                    return false;
                }

                return (startTime < range.end && currentTime > range.start);
            });
        }

        return false;
    };

    React.useEffect(() => {
        if (value) {
            const { fromHour, fromMinute, toHour, toMinute } = value;
            if (fromHour !== undefined && fromMinute !== undefined && toHour !== undefined && toMinute !== undefined) {
                setStart(`${fromHour},${fromMinute}`);
                setEnd(`${toHour},${toMinute}`);
            }
        }
    }, [value]);

    const options = useMemo(() => {
        const timeOptions: TimeOption[] = [];
        for (let i = 0; i < 12; i++) {
            for (let j = 0; j < time.length; j++) {
                const label = `${i >= 10 ? i : `0${i}`}:${j === 0 ? '00' : time[j]} AM`;
                timeOptions.push({
                    label,
                    value: [i, time[j]],
                });
            }
        }
        for (let j = 0; j < time.length; j++) {
            const label = `12:${j === 0 ? '00' : time[j]} PM`;
            timeOptions.push({
                label,
                value: [12, time[j]],
            });
        }
        for (let i = 1; i < 12; i++) {
            for (let j = 0; j < time.length; j++) {
                const label = `${i >= 10 ? i : `0${i}`}:${j === 0 ? '00' : time[j]} PM`;
                timeOptions.push({
                    label,
                    value: [12 + i, time[j]],
                });
            }
        }
        return timeOptions;
    }, []);

    return (
        <div className={s.wrap}>
            <div className={s.timePicker}>
                <Select
                    onChange={(val) => {
                        setStart(val);
                        const startVal = (val && val.split(',')) || [];
                        const endVal = (end && end.split(',')) || [];
                        let useEnd = end;
                        let needChange = false;
                        if (!useEnd) {
                            needChange = true;
                        }
                        if (endVal.length > 0 && startVal.length === 2) {
                            if (parseInt(endVal[0], 10) < parseInt(startVal[0], 10)) {
                                needChange = true;
                            } else if (parseInt(endVal[0], 10) === parseInt(startVal[0], 10)) {
                                if (parseInt(endVal[1], 10) <= parseInt(startVal[1], 10)) {
                                    needChange = true;
                                }
                            }
                        }
                        if (needChange && startVal.length === 2) {
                            const newEndValues = [0, 0];
                            if (startVal[0] === '23' && startVal[1] === '30') {
                                newEndValues[0] = 23;
                                newEndValues[1] = 45;
                            } else if (parseInt(startVal[1], 10) === 0) {
                                newEndValues[0] = parseInt(startVal[0], 10);
                                newEndValues[1] = 30;
                            } else if (parseInt(startVal[1], 10) === 15) {
                                newEndValues[0] = parseInt(startVal[0], 10);
                                newEndValues[1] = 45;
                            } else if (parseInt(startVal[1], 10) === 30) {
                                newEndValues[0] = parseInt(startVal[0], 10) + 1;
                                newEndValues[1] = 0;
                            } else if (parseInt(startVal[1], 10) === 45) {
                                newEndValues[0] = parseInt(startVal[0], 10) + 1;
                                newEndValues[1] = 15;
                            }
                            useEnd = newEndValues.join(',');
                            const newEndVal = (useEnd && useEnd.split(',')) || [];
                            const updateVal = {
                                fromHour: parseInt(startVal[0], 10),
                                fromMinute: parseInt(startVal[1], 10),
                                toHour: parseInt(newEndVal[0], 10),
                                toMinute: parseInt(newEndVal[1], 10),
                            };
                            if (startVal.length > 0 && newEndVal.length > 0 && !isEndTimeDisabled(updateVal.toHour, updateVal.toMinute)) {
                                setEnd(newEndValues.join(','));
                                onChange?.(updateVal);
                            }
                            return;
                        }
                        if (startVal.length > 0 && endVal.length > 0) {
                            onChange?.({
                                fromHour: parseInt(startVal[0], 10),
                                fromMinute: parseInt(startVal[1], 10),
                                toHour: parseInt(endVal[0], 10),
                                toMinute: parseInt(endVal[1], 10),
                            });
                        }
                    }}
                    className={s.timeSelect}
                    value={start}
                >
                    {
                        options.slice(0, -1).map((option) => {
                            const isOverlapDisabled = isStartTimeDisabled(option.value[0], option.value[1]);
                            return (
                                <Select.Option
                                    key={option.label}
                                    value={option.value.join(',')}
                                    disabled={isOverlapDisabled}
                                >
                                    {option.label}
                                </Select.Option>
                            );
                        })
                    }
                </Select>
                <span className="ml-4 mr-4">to</span>
                <Select
                    onChange={(val) => {
                        setEnd(val);
                        const startVal = (start && start.split(',')) || [];
                        const endVal = (val && val.split(',')) || [];
                        if (startVal.length > 0 && endVal.length > 0) {
                            onChange?.({
                                fromHour: parseInt(startVal[0], 10),
                                fromMinute: parseInt(startVal[1], 10),
                                toHour: parseInt(endVal[0], 10),
                                toMinute: parseInt(endVal[1], 10),
                            });
                        }
                    }}
                    className={s.timeSelect}
                    value={end}
                >
                    {
                        options.map((option) => {
                            let disable = false;
                            if (start) {
                                const startValues = start.split(',');
                                if (parseInt(startValues[0], 10) > option.value[0]) {
                                    disable = true;
                                } else if (parseInt(startValues[0], 10) === option.value[0]) {
                                    disable = parseInt(startValues[1], 10) >= option.value[1];
                                }
                            }
                            const isOverlapDisabled = isEndTimeDisabled(option.value[0], option.value[1]);
                            return (
                                <Select.Option
                                    key={option.label}
                                    value={option.value.join(',')}
                                    disabled={disable || isOverlapDisabled}
                                >
                                    {option.label}
                                </Select.Option>
                            );
                        })
                    }
                </Select>
            </div>
        </div>
    );
};

export default TimePicker;
